import clsx from "~/util/html/style";
import { createMemo, For, splitProps, type ComponentProps } from "solid-js";
import { makeInputClass } from "~/common/input";

const NO_ARROW_STYLE = { "background-image": "none", "padding-right": "0", "padding-left": "0" };

// ghost: compact select that reads as plain text until hovered — for table cells where a
// bordered box per row is too loud
export function Select(_props: ComponentProps<"select"> & { compact?: boolean; ghost?: boolean }) {
  const [local, rest] = splitProps(_props, ["class", "compact", "ghost"]);

  return (
    <select
      class={clsx(
        local.compact
          ? clsx(
              "rounded-lg border py-0 text-sm outline-none focus-visible:ring-2 focus-visible:ring-brand-focus disabled:bg-brand-disabled",
              local.ghost
                ? "border-transparent bg-transparent hover:border-brand-subtle"
                : "border-brand-subtle bg-white text-center",
            )
          : makeInputClass({ additionalClasses: "h-input", ownStateOnly: true }),
        // .ui-select needed to prevent global select styles from applying
        "ui-select pr-10",
        local.class,
      )}
      style={local.compact ? NO_ARROW_STYLE : undefined}
      {...rest}
    />
  );
}

type SelectOption = { value: string; label: string };

/**
 * A select's `<option>`s, kept per value across refreshes. A refetch hands over new option
 * objects even when nothing changed: `<For>` over them would rebuild every option and the
 * browser would fall back to the first one, and `<Index>` would keep the selected position,
 * so dropping an earlier entry shows the next one. Either way the select shows something
 * other than the value its signal holds and sends.
 */
export function SelectOptions(props: { options: readonly SelectOption[] }) {
  const values = createMemo(() => props.options.map((option) => option.value));
  const labels = createMemo(
    () => new Map(props.options.map((option) => [option.value, option.label])),
  );

  return (
    <For each={values()}>{(value) => <option value={value}>{labels().get(value)}</option>}</For>
  );
}
